import type {
  AgenticCartBundleSummaryResponse,
  CreateAgenticCartOrderResponse,
} from "@ishvaram/api-client";
import {
  ISHVARAM_COMMERCE_SYSTEM,
  ISHVARAM_NATIVE_CART_CHECKOUT_CHANNEL,
} from "@/lib/commerce-attribution";
import type { CheckoutChannel } from "@/lib/checkout-log-domain";
import type { CommerceSystem } from "@/lib/commerce-routing";

export const AGENTIC_CART_SOURCE = "agentic_cart";
export const AGENTIC_CART_PLACEMENT = "agentic_cart_checkout";
export const AGENTIC_CART_CHECKOUT_CHANNEL = ISHVARAM_NATIVE_CART_CHECKOUT_CHANNEL;
export const AGENTIC_CART_COMMERCE_SYSTEM = ISHVARAM_COMMERCE_SYSTEM;
export const AGENTIC_CART_OFFER_TYPE = "product";
export const AGENTIC_CART_PLAN_TYPE = "report";
export const AGENTIC_CART_SELECTED_OFFER_REASON = "native_agentic_bundle_checkout";

export type AgenticCartAttributionContext = {
  bundle: AgenticCartBundleSummaryResponse;
  sourcePath: string;
  intent: string;
  customerProblem: string;
  agentTraceId?: string;
  journeyActionId?: string;
  concernId?: string;
  eventId?: string;
};

export type AgenticCartOrderAttributionContext = AgenticCartAttributionContext & {
  order: CreateAgenticCartOrderResponse;
};

type AgenticCartAttributionPayload = {
  source: typeof AGENTIC_CART_SOURCE;
  placement: typeof AGENTIC_CART_PLACEMENT;
  checkout_channel: CheckoutChannel;
  commerce_system: CommerceSystem;
  intent: string;
  source_path: string;
  customer_problem: string;
  agent_trace_id?: string;
  journey_action_id?: string;
  concern_id?: string;
};

type AgenticCartRazorpayPayload = AgenticCartAttributionPayload & {
  order_id: string;
  plan_id: string;
  plan_type: typeof AGENTIC_CART_PLAN_TYPE;
  price_inr: number;
};

type AgenticCartRevenuePayload = AgenticCartAttributionPayload & {
  revenue_inr: number;
  order_id: string;
  offer_type: typeof AGENTIC_CART_OFFER_TYPE;
  plan_id: string;
};

type AgenticCartMemoryPayload = {
  type: "checkout_started" | "checkout_abandoned" | "purchase_completed";
  traceId?: string;
  sessionId?: string;
  sourcePath: string;
  seoIntent: string;
  customerProblem: string;
  offerType: typeof AGENTIC_CART_OFFER_TYPE;
  planId: string;
  bundleId: string;
  checkoutChannel: CheckoutChannel;
  commerceSystem: CommerceSystem;
  priceInr: number;
  orderId: string;
};

export function agenticCartTrackingBase(bundle: AgenticCartBundleSummaryResponse) {
  return {
    bundle_id: bundle.bundle_id,
    plan_id: bundle.report_plan_id,
    price_inr: bundle.price_inr,
    checkout_channel: AGENTIC_CART_CHECKOUT_CHANNEL,
    commerce_system: AGENTIC_CART_COMMERCE_SYSTEM,
    offer_type: AGENTIC_CART_OFFER_TYPE,
    product_skus: bundle.line_items
      .filter((item) => item.kind === "physical_product")
      .map((item) => item.reference_id),
  };
}

export function agenticCartAttribution(
  ctx: AgenticCartAttributionContext
): AgenticCartAttributionPayload {
  return {
    source: AGENTIC_CART_SOURCE,
    placement: AGENTIC_CART_PLACEMENT,
    checkout_channel: AGENTIC_CART_CHECKOUT_CHANNEL,
    commerce_system: AGENTIC_CART_COMMERCE_SYSTEM,
    intent: ctx.intent,
    source_path: ctx.sourcePath,
    customer_problem: ctx.customerProblem,
    agent_trace_id: ctx.agentTraceId,
    journey_action_id: ctx.journeyActionId,
    concern_id: ctx.concernId,
  };
}

export function agenticCartReportPayload(ctx: AgenticCartAttributionContext) {
  return {
    plan_id: ctx.bundle.report_plan_id,
    price_inr: ctx.bundle.price_inr,
    ...agenticCartAttribution(ctx),
  };
}

export function agenticCartRazorpayPayload(
  ctx: AgenticCartOrderAttributionContext
): AgenticCartRazorpayPayload {
  return {
    order_id: ctx.order.razorpay_order_id,
    plan_id: ctx.bundle.report_plan_id,
    plan_type: AGENTIC_CART_PLAN_TYPE,
    price_inr: ctx.bundle.price_inr,
    ...agenticCartAttribution(ctx),
  };
}

export function agenticCartRevenuePayload(
  ctx: AgenticCartOrderAttributionContext
): AgenticCartRevenuePayload {
  return {
    revenue_inr: ctx.bundle.price_inr,
    order_id: ctx.order.razorpay_order_id,
    offer_type: AGENTIC_CART_OFFER_TYPE,
    plan_id: ctx.bundle.report_plan_id,
    ...agenticCartAttribution(ctx),
  };
}

export function agenticCartMemoryPayload(
  ctx: AgenticCartOrderAttributionContext,
  type: "checkout_started" | "checkout_abandoned" | "purchase_completed",
  sessionId?: string
): AgenticCartMemoryPayload {
  return {
    type,
    traceId: ctx.agentTraceId,
    sessionId,
    sourcePath: ctx.sourcePath,
    seoIntent: ctx.intent,
    customerProblem: ctx.customerProblem,
    offerType: AGENTIC_CART_OFFER_TYPE,
    planId: ctx.bundle.report_plan_id,
    bundleId: ctx.bundle.bundle_id,
    checkoutChannel: AGENTIC_CART_CHECKOUT_CHANNEL,
    commerceSystem: AGENTIC_CART_COMMERCE_SYSTEM,
    priceInr: ctx.bundle.price_inr,
    orderId: ctx.order.razorpay_order_id,
  };
}

export function agenticCartTrackingParams(
  ctx: AgenticCartAttributionContext | AgenticCartOrderAttributionContext
) {
  const order = "order" in ctx ? ctx.order : undefined;
  return {
    ...agenticCartTrackingBase(ctx.bundle),
    ...agenticCartAttribution(ctx),
    order_id: order?.razorpay_order_id,
    cart_id: order?.cart_id,
    amount_paisa: order?.amount_paisa,
    fulfillment_mirror_required: order?.fulfillment_mirror_required,
    product_count: ctx.bundle.line_items.filter((item) => item.kind === "physical_product").length,
  };
}
